/**
 * Scheduling Hook
 * Custom hook for managing report scheduling
 */

import { useState, useCallback, useEffect } from 'react';
import SchedulingService from '../services/SchedulingService';

export const useScheduling = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load schedules on mount
  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const scheduledReports = SchedulingService.getScheduledReports();
      setSchedules(scheduledReports);
    } catch (err) {
      console.error('Failed to load schedules:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createSchedule = useCallback(async (config) => {
    try {
      setLoading(true);
      setError(null);

      const scheduledReport = await SchedulingService.scheduleReport(config);
      setSchedules(prev => [...prev, scheduledReport]);
      
      return scheduledReport;
    } catch (err) {
      console.error('Failed to create schedule:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSchedule = useCallback(async (scheduleId, updates) => {
    try {
      setLoading(true);
      setError(null);

      const updatedReport = await SchedulingService.updateScheduledReport(scheduleId, updates);
      setSchedules(prev => 
        prev.map(schedule => 
          schedule.id === scheduleId ? updatedReport : schedule
        )
      );
      
      return updatedReport;
    } catch (err) {
      console.error('Failed to update schedule:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSchedule = useCallback(async (scheduleId) => {
    try {
      setLoading(true);
      setError(null);

      await SchedulingService.deleteScheduledReport(scheduleId);
      setSchedules(prev => prev.filter(schedule => schedule.id !== scheduleId));
      
      return true;
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleSchedule = useCallback(async (scheduleId, enabled) => {
    try {
      setError(null);

      const updatedReport = await SchedulingService.toggleScheduledReport(scheduleId, enabled);
      setSchedules(prev => 
        prev.map(schedule => 
          schedule.id === scheduleId ? updatedReport : schedule
        )
      );
      
      return updatedReport;
    } catch (err) {
      console.error('Failed to toggle schedule:', err);
      setError(err.message);
      return null;
    }
  }, []);

  const triggerSchedule = useCallback(async (scheduleId) => {
    try {
      setLoading(true);
      setError(null);

      const result = await SchedulingService.triggerScheduledReport(scheduleId);
      
      // Refresh schedules to get updated run counts
      await loadSchedules();
      
      return result;
    } catch (err) {
      console.error('Failed to trigger schedule:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadSchedules]);

  const getSchedulingStats = useCallback(() => {
    return SchedulingService.getSchedulingStats();
  }, []);

  const getDueReports = useCallback(() => {
    return SchedulingService.getDueReports();
  }, []);

  const getSchedulingHistory = useCallback((limit = 20) => {
    return SchedulingService.getSchedulingHistory(limit);
  }, []);

  const exportSchedules = useCallback(() => {
    return SchedulingService.exportScheduledReports();
  }, []);

  const importSchedules = useCallback(async (exportData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await SchedulingService.importScheduledReports(exportData);
      await loadSchedules(); // Refresh schedules
      
      return result;
    } catch (err) {
      console.error('Failed to import schedules:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadSchedules]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    schedules,
    loading,
    error,

    // Actions
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleSchedule,
    triggerSchedule,
    loadSchedules,

    // Utilities
    getSchedulingStats,
    getDueReports,
    getSchedulingHistory,
    exportSchedules,
    importSchedules,
    clearError,

    // Status helpers
    hasSchedules: schedules.length > 0,
    hasError: !!error,
    enabledCount: schedules.filter(s => s.enabled).length,
    disabledCount: schedules.filter(s => !s.enabled).length
  };
};

// Hook for schedule form management
export const useScheduleForm = (initialSchedule = null) => {
  const [formData, setFormData] = useState({
    name: '',
    reportType: 'daily-log',
    parameters: {},
    schedule: {
      frequency: 'daily',
      time: '09:00',
      daysOfWeek: [1], // Monday
      dayOfMonth: 1
    },
    format: 'PDF',
    recipients: [],
    enabled: true,
    ...initialSchedule
  });

  const [errors, setErrors] = useState({});
  const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => {
    if (initialSchedule) {
      setFormData(prev => ({ ...prev, ...initialSchedule }));
    }
  }, [initialSchedule]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for updated field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const updateScheduleField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }));
  }, []);

  const addRecipient = useCallback((email) => {
    if (email && !formData.recipients.includes(email)) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, email]
      }));
      setRecipientInput('');
    }
  }, [formData.recipients]);

  const removeRecipient = useCallback((email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Schedule name is required';
    }

    if (!formData.reportType) {
      newErrors.reportType = 'Report type is required';
    }

    if (!formData.schedule.time) {
      newErrors.time = 'Time is required';
    }

    if (formData.recipients.length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    }

    if (formData.schedule.frequency === 'weekly' && formData.schedule.daysOfWeek.length === 0) {
      newErrors.daysOfWeek = 'At least one day must be selected for weekly schedule';
    }

    if (formData.schedule.frequency === 'monthly' && (!formData.schedule.dayOfMonth || formData.schedule.dayOfMonth < 1 || formData.schedule.dayOfMonth > 31)) {
      newErrors.dayOfMonth = 'Valid day of month is required (1-31)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      reportType: 'daily-log',
      parameters: {},
      schedule: {
        frequency: 'daily',
        time: '09:00',
        daysOfWeek: [1],
        dayOfMonth: 1
      },
      format: 'PDF',
      recipients: [],
      enabled: true
    });
    setErrors({});
    setRecipientInput('');
  }, []);

  return {
    // Form state
    formData,
    errors,
    recipientInput,

    // Form actions
    updateField,
    updateScheduleField,
    addRecipient,
    removeRecipient,
    setRecipientInput,
    validateForm,
    resetForm,

    // Status
    isValid: Object.keys(errors).length === 0,
    hasChanges: JSON.stringify(formData) !== JSON.stringify(initialSchedule || {}),
    hasRecipients: formData.recipients.length > 0
  };
};

// Hook for schedule monitoring
export const useScheduleMonitor = (refreshInterval = 60000) => {
  const [dueReports, setDueReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);

  const refreshData = useCallback(() => {
    setDueReports(SchedulingService.getDueReports());
    setStats(SchedulingService.getSchedulingStats());
    setHistory(SchedulingService.getSchedulingHistory(10));
  }, []);

  useEffect(() => {
    refreshData();
    
    const interval = setInterval(refreshData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshData, refreshInterval]);

  return {
    dueReports,
    stats,
    history,
    refreshData,
    hasDueReports: dueReports.length > 0
  };
};